import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { getQuizPerformanceBreakdown } from '../../../services/quizPerformanceService';
import { useAuth } from '../../../contexts/AuthContext';

const QuizPerformanceBreakdown = ({ playId, onClose }) => {
  const { user } = useAuth();
  const [performanceData, setPerformanceData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    if (user?.id && playId) {
      loadPerformanceData();
    }
  }, [user?.id, playId]);

  const loadPerformanceData = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getQuizPerformanceBreakdown(user?.id, playId);
      setPerformanceData(data);
    } catch (err) {
      console.error('Error loading performance data:', err);
      setError(err?.message || 'Failed to load performance data');
    } finally {
      setLoading(false);
    }
  };

  const getMasteryColor = (level) => {
    switch (level) {
      case 'mastered': return 'text-success';
      case 'proficient': return 'text-primary';
      case 'practicing': return 'text-warning';
      case 'learning': return 'text-muted-foreground';
      default: return 'text-muted-foreground';
    }
  };

  const getMasteryIcon = (level) => {
    switch (level) {
      case 'mastered': return 'CheckCircle2';
      case 'proficient': return 'TrendingUp';
      case 'practicing': return 'Target';
      case 'learning': return 'BookOpen';
      default: return 'Circle';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 1: return 'border-destructive bg-destructive/10';
      case 2: return 'border-warning bg-warning/10';
      case 3: return 'border-primary bg-primary/10';
      default: return 'border-border bg-card';
    }
  };

  const getPriorityLabel = (priority) => {
    switch (priority) {
      case 1: return 'High Priority';
      case 2: return 'Medium Priority';
      case 3: return 'Low Priority';
      default: return 'Review';
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center">
        <div className="bg-card border border-border rounded-lg p-8 max-w-4xl w-full mx-4">
          <div className="flex items-center justify-center">
            <Icon name="Loader" size={48} className="text-primary animate-spin" />
          </div>
        </div>
      </div>
    );
  }

  if (error || !performanceData) {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center">
        <div className="bg-card border border-border rounded-lg p-8 max-w-4xl w-full mx-4">
          <div className="text-center">
            <Icon name="AlertCircle" size={48} className="text-destructive mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-foreground mb-2">Error Loading Data</h3>
            <p className="text-muted-foreground mb-4">{error || 'Failed to load performance data'}</p>
            <Button onClick={onClose} variant="outline">Close</Button>
          </div>
        </div>
      </div>
    );
  }

  const { phonemeMastery, performanceSummary, accuracyTrends, recommendations, overallStats } = performanceData;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-card border border-border rounded-lg max-w-6xl w-full my-8 max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border bg-gradient-to-r from-primary to-secondary">
          <div>
            <h2 className="text-2xl font-bold text-primary-foreground mb-1">Quiz Performance Breakdown</h2>
            <p className="text-sm text-primary-foreground/80">Detailed analysis of your pronunciation progress</p>
          </div>
          <button
            onClick={onClose}
            className="text-primary-foreground/80 hover:text-primary-foreground transition-colors"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-border px-6 bg-muted/30">
          {['overview', 'phonemes', 'trends', 'recommendations']?.map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-3 text-sm font-medium transition-colors border-b-2 ${
                activeTab === tab
                  ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {tab?.charAt(0)?.toUpperCase() + tab?.slice(1)}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Overall Statistics */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-primary/10 border border-primary/20 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Icon name="Target" size={20} className="text-primary" />
                    <span className="text-sm text-muted-foreground">Mastery Rate</span>
                  </div>
                  <div className="text-2xl font-bold text-foreground">{overallStats?.masteryPercentage}%</div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {overallStats?.masteredPhonemes} of {overallStats?.totalPhonemes} phonemes
                  </div>
                </div>

                <div className="bg-success/10 border border-success/20 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Icon name="TrendingUp" size={20} className="text-success" />
                    <span className="text-sm text-muted-foreground">Avg Accuracy</span>
                  </div>
                  <div className="text-2xl font-bold text-foreground">{overallStats?.avgAccuracy}%</div>
                  <div className="text-xs text-muted-foreground mt-1">
                    Across all phonemes
                  </div>
                </div>

                <div className="bg-accent/10 border border-accent/20 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Icon name="Flame" size={20} className="text-accent" />
                    <span className="text-sm text-muted-foreground">Practice Streak</span>
                  </div>
                  <div className="text-2xl font-bold text-foreground">{overallStats?.practiceStreak}</div>
                  <div className="text-xs text-muted-foreground mt-1">
                    Consecutive days
                  </div>
                </div>

                <div className="bg-secondary/10 border border-secondary/20 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Icon name="Award" size={20} className="text-secondary" />
                    <span className="text-sm text-muted-foreground">Quiz Pass Rate</span>
                  </div>
                  <div className="text-2xl font-bold text-foreground">{overallStats?.quizPassRate}%</div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {overallStats?.passedQuizzes} of {overallStats?.totalQuizAttempts} quizzes
                  </div>
                </div>
              </div>

              {/* Category Performance */}
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-4">Performance by Category</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {performanceSummary?.map((category) => (
                    <div key={category?.phonemeCategory} className="bg-card border border-border rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-base font-semibold text-foreground capitalize">
                          {category?.phonemeCategory}s
                        </h4>
                        <span className="text-sm text-muted-foreground">
                          {category?.totalPhonemes} phonemes
                        </span>
                      </div>
                      
                      <div className="space-y-2">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-muted-foreground">Average Accuracy</span>
                          <span className="font-semibold text-foreground">{category?.avgAccuracy?.toFixed(1)}%</span>
                        </div>
                        
                        <div className="w-full bg-muted rounded-full h-2">
                          <div
                            className="bg-primary rounded-full h-2 transition-all"
                            style={{ width: `${category?.avgAccuracy || 0}%` }}
                          />
                        </div>

                        <div className="grid grid-cols-2 gap-2 mt-3 text-xs">
                          <div className="flex items-center gap-1">
                            <Icon name="CheckCircle2" size={14} className="text-success" />
                            <span className="text-muted-foreground">{category?.masteredCount} mastered</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Icon name="TrendingUp" size={14} className="text-primary" />
                            <span className="text-muted-foreground">{category?.proficientCount} proficient</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'phonemes' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-4">Phoneme Mastery Details</h3>
                
                {Object.entries(phonemeMastery?.byCategory || {})?.map(([category, data]) => (
                  <div key={category} className="mb-6">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-base font-semibold text-foreground capitalize">{category}s</h4>
                      <span className="text-sm text-muted-foreground">
                        {data?.masteryRate}% mastered
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {data?.phonemes?.map((phoneme, index) => (
                        <div key={index} className="bg-card border border-border rounded-lg p-3">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-lg font-mono font-semibold text-foreground">
                              {phoneme?.phoneme}
                            </span>
                            <Icon 
                              name={getMasteryIcon(phoneme?.masteryLevel)} 
                              size={18} 
                              className={getMasteryColor(phoneme?.masteryLevel)}
                            />
                          </div>
                          
                          <div className="space-y-1 text-xs">
                            <div className="flex items-center justify-between">
                              <span className="text-muted-foreground">Accuracy</span>
                              <span className="font-semibold text-foreground">
                                {phoneme?.accuracyScore?.toFixed(1)}%
                              </span>
                            </div>
                            
                            <div className="w-full bg-muted rounded-full h-1.5">
                              <div
                                className="bg-primary rounded-full h-1.5 transition-all"
                                style={{ width: `${phoneme?.accuracyScore || 0}%` }}
                              />
                            </div>

                            <div className="flex items-center justify-between pt-1">
                              <span className={`capitalize ${getMasteryColor(phoneme?.masteryLevel)}`}>
                                {phoneme?.masteryLevel}
                              </span>
                              {phoneme?.improvementRate > 0 && (
                                <span className="text-success flex items-center gap-1">
                                  <Icon name="TrendingUp" size={10} />
                                  +{phoneme?.improvementRate?.toFixed(1)}%
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'trends' && (
            <div className="space-y-6">
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-foreground">Pronunciation Accuracy Trends</h3>
                  <div className="flex items-center gap-2">
                    <Icon 
                      name={accuracyTrends?.overallTrend === 'improving' ? 'TrendingUp' : 'TrendingDown'} 
                      size={18} 
                      className={accuracyTrends?.overallTrend === 'improving' ? 'text-success' : 'text-warning'}
                    />
                    <span className={`text-sm font-semibold ${
                      accuracyTrends?.overallTrend === 'improving' ? 'text-success' : 'text-warning'
                    }`}>
                      {accuracyTrends?.overallTrend === 'improving' ? '+' : ''}
                      {accuracyTrends?.trendPercentage?.toFixed(1)}%
                    </span>
                  </div>
                </div>

                <div className="bg-card border border-border rounded-lg p-4">
                  <div className="space-y-3">
                    {accuracyTrends?.dailyProgress?.slice(0, 10)?.map((day, index) => (
                      <div key={index} className="flex items-center gap-4">
                        <span className="text-xs text-muted-foreground w-20">
                          {new Date(day?.date)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </span>
                        
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm text-foreground">{day?.averageScore?.toFixed(1)}%</span>
                            <span className="text-xs text-muted-foreground">
                              {day?.totalAttempts} attempts
                            </span>
                          </div>
                          <div className="w-full bg-muted rounded-full h-2">
                            <div
                              className="bg-primary rounded-full h-2 transition-all"
                              style={{ width: `${day?.averageScore || 0}%` }}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {accuracyTrends?.dailyProgress?.[0]?.commonMistakes?.length > 0 && (
                <div>
                  <h4 className="text-base font-semibold text-foreground mb-3">Recent Common Mistakes</h4>
                  <div className="bg-card border border-border rounded-lg p-4">
                    <ul className="space-y-2">
                      {accuracyTrends?.dailyProgress?.[0]?.commonMistakes?.slice(0, 5)?.map((mistake, index) => (
                        <li key={index} className="flex items-start gap-2 text-sm">
                          <Icon name="AlertCircle" size={16} className="text-warning flex-shrink-0 mt-0.5" />
                          <span className="text-muted-foreground">{mistake}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'recommendations' && (
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-4">Personalized Recommendations</h3>
                <p className="text-sm text-muted-foreground mb-6">
                  Based on your recent performance, here are the areas that need your attention
                </p>

                <div className="space-y-4">
                  {recommendations?.map((rec, index) => (
                    <div 
                      key={index} 
                      className={`border rounded-lg p-4 ${getPriorityColor(rec?.priority)}`}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <span className="text-xl font-mono font-bold text-foreground">
                            {rec?.phoneme}
                          </span>
                          <div>
                            <span className="text-xs text-muted-foreground capitalize">
                              {rec?.phonemeCategory}
                            </span>
                            <div className="flex items-center gap-2 mt-1">
                              <span className="text-sm font-semibold text-foreground">
                                {rec?.accuracyScore?.toFixed(1)}%
                              </span>
                              <Icon 
                                name={getMasteryIcon(rec?.masteryLevel)} 
                                size={14} 
                                className={getMasteryColor(rec?.masteryLevel)}
                              />
                              <span className={`text-xs ${getMasteryColor(rec?.masteryLevel)}`}>
                                {rec?.masteryLevel}
                              </span>
                            </div>
                          </div>
                        </div>
                        <span className={`text-xs font-semibold px-2 py-1 rounded ${
                          rec?.priority === 1 ? 'bg-destructive text-destructive-foreground' :
                          rec?.priority === 2 ? 'bg-warning text-warning-foreground': 'bg-primary text-primary-foreground'
                        }`}>
                          {getPriorityLabel(rec?.priority)}
                        </span>
                      </div>

                      <p className="text-sm text-foreground mb-3">
                        {rec?.recommendation}
                      </p>

                      <div className="flex items-center gap-4 text-xs text-muted-foreground">
                        <div className="flex items-center gap-1">
                          <Icon name="Activity" size={12} />
                          <span>{rec?.attemptCount} attempts</span>
                        </div>
                        {rec?.improvementRate > 0 && (
                          <div className="flex items-center gap-1 text-success">
                            <Icon name="TrendingUp" size={12} />
                            <span>+{rec?.improvementRate?.toFixed(1)}% improvement</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 mt-6">
                <div className="flex items-start gap-3">
                  <Icon name="Lightbulb" size={20} className="text-primary flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-sm font-semibold text-foreground mb-2">Practice Tips</h4>
                    <ul className="space-y-1 text-xs text-muted-foreground">
                      <li>• Practice high-priority phonemes for 10-15 minutes daily</li>
                      <li>• Listen to audio examples multiple times before attempting</li>
                      <li>• Record yourself and compare with native pronunciation</li>
                      <li>• Focus on one phoneme category at a time for better retention</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-border p-4 bg-muted/30">
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">
              Last updated: {new Date()?.toLocaleDateString('en-US', { 
                month: 'long', 
                day: 'numeric', 
                year: 'numeric' 
              })}
            </span>
            <Button onClick={onClose} variant="outline" size="sm">
              Close
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuizPerformanceBreakdown;